import { Tabs } from "antd";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useIntl } from "react-intl";

import { AgentAPI, PermissionsAPI } from "@/api";
import Query from "@/models/query";
import { useGlobalStore } from "@/stores";
import ErrorsFormatter from "@/ui/_common_/errors-formatter";
import { FormSpinner } from "@/ui/_common_/form-spinner";
import { Bonuses } from "@/ui/bonuses";
import { Games } from "@/ui/games";
import { JackpotSettingsForm } from "@/ui/jackpots";
import { AgentLobbySettings } from "@/ui/lobby";
import WrapperTab from "@/ui/pages/agents/edit/tabs/wrapper-tab";
import { PSPSetting } from "@/ui/psp-setting/form";
import { RulesTabs } from "@/ui/rules";
import { SportSettings } from "@/ui/sport-settings/form";
import { Sweepstakes } from "@/ui/sweepstakes";
import { Websites } from "@/ui/websites";
import { isTruthy } from "@/utilities";

import { AgentOtherSettings } from "../agent-other-settings";
import { GeneralForm } from "../general-form";
import { PosSettings, PosTemplate } from "../pos";

interface Props {
  agentId: number;

  setAgentName(name: string): void;
}

export const EditAgentTabs = observer<Props>(({ agentId, setAgentName }) => {
  const intl = useIntl();
  const { permissionsStore } = useGlobalStore();

  const role = useMemo(() => {
    const { userStore } = useGlobalStore();
    return userStore.user.role;
  }, []);

  const { agentQuery, parentAgentQuery } = useMemo(() => {
    const agentQuery = new Query(AgentAPI.getById);
    const parentAgentQuery = new Query(AgentAPI.getById);

    agentQuery.submit({ id: agentId }).then(async () => {
      const agent = agentQuery.data!;

      if (agent.parentAgentId) {
        await parentAgentQuery.submit({ id: agent.parentAgentId });
      }

      setAgentName(agent.name);
    });

    return { agentQuery, parentAgentQuery };
  }, [agentId, setAgentName]);

  const permissionsQuery = useMemo(() => {
    const query = new Query(PermissionsAPI.get);
    query.submit({ agentId });
    return query;
  }, [agentId]);

  if (
    agentQuery.isIdle ||
    agentQuery.isPending ||
    permissionsQuery.isIdle ||
    permissionsQuery.isPending ||
    parentAgentQuery.isPending
  ) {
    return <FormSpinner />;
  }

  if (permissionsQuery.isRejected || agentQuery.isRejected) {
    return (
      <ErrorsFormatter
        queries={[permissionsQuery, agentQuery, parentAgentQuery]}
      />
    );
  }

  const agent = agentQuery.data;
  const parentAgent = parentAgentQuery.data;

  return (
    <Tabs
      destroyInactiveTabPane={true}
      items={[
        {
          key: "general",
          label: intl.formatMessage({ defaultMessage: "General" }),
          children: <GeneralForm agent={agent} parentAgent={parentAgent} />,
        },
        permissionsQuery.data.lobby && {
          key: "lobby",
          label: intl.formatMessage({ defaultMessage: "Lobby" }),
          children: <AgentLobbySettings agentId={agentId} />,
        },
        permissionsQuery.data.websites && {
          key: "websites",
          label: intl.formatMessage({ defaultMessage: "Websites" }),
          children: <Websites.EditForm entity={{ agentId }} />,
        },
        permissionsQuery.data.games && {
          key: "games",
          label: intl.formatMessage({ defaultMessage: "Games" }),
          children: <Games.EditForm entity={{ agentId }} />,
        },
        permissionsQuery.data.bonuses && {
          key: "bonuses",
          label: intl.formatMessage({ defaultMessage: "Bonuses" }),
          className: "subtabs",
          children: (
            <WrapperTab agentId={agentId}>
              <Bonuses.Tabs entity={{ agentId }} />
            </WrapperTab>
          ),
        },
        permissionsQuery.data.jackpots && {
          key: "jackpots",
          label: intl.formatMessage({ defaultMessage: "Jackpots" }),
          children: (
            <WrapperTab agentId={agentId}>
              <JackpotSettingsForm entity={{ agentId }} />
            </WrapperTab>
          ),
        },
        permissionsQuery.data.sweepstakes && {
          key: "sweepstakes",
          label: intl.formatMessage({ defaultMessage: "Sweepstakes" }),
          children: <Sweepstakes.EditForm entity={{ agentId }} />,
        },
        permissionsQuery.data.other && {
          key: "other",
          label: intl.formatMessage({ defaultMessage: "Other" }),
          children: <AgentOtherSettings agentId={agentId} />,
        },
        permissionsQuery.data.rulesAndTerms && {
          key: "rules-and-terms",
          className: "subtabs",
          label: intl.formatMessage({ defaultMessage: "Rules and Terms" }),
          children: <RulesTabs entity={{ agentId }} />,
        },
        permissionsQuery.data.pos && {
          key: "pos-settings",
          label: intl.formatMessage({ defaultMessage: "POS Settings" }),
          children: <PosSettings entity={{ agentId }} />,
        },
        permissionsQuery.data.pos && {
          key: "pos-template",
          label: intl.formatMessage({ defaultMessage: "POS Template" }),
          children: <PosTemplate entity={{ agentId }} />,
        },

        permissionsStore.has("ViewSportSettings") && {
          key: "special",
          label: intl.formatMessage({ defaultMessage: "Special" }),
          children: <SportSettings entity={{ agentId }} />,
        },
        ["Admin", "Manager", "ClientAdmin"].includes(role) && {
          key: "psp",
          label: intl.formatMessage({ defaultMessage: "PSP" }),
          children: (
            <PSPSetting
              entity={{ agentId }}
              readonly={["ClientAdmin"].includes(role)}
            />
          ),
        },
      ].filter(isTruthy)}
    />
  );
});
